import React from 'react'
import { type Tick, rulerTickReactKey } from '@/lib/ruler/compute-ruler-ticks'
import { H_RULER_THICKNESS_PX, V_RULER_THICKNESS_PX } from '../constants/ruler-size'
import { rulerTickLength } from '../helpers/ruler-ticks'

type RulerAxis = 'horizontal' | 'vertical'

// Tick sizes for 16px ruler: major = full, mid = 8px, minor = 4px
const getHorizontalTickHeight = (tick: Tick) => rulerTickLength(tick, H_RULER_THICKNESS_PX)
const getVerticalTickWidth = (tick: Tick) => rulerTickLength(tick, V_RULER_THICKNESS_PX)

// Horizontal ruler ticks (offset by vertical ruler width so ticks align with scroller content).
// Ticks extend from the bottom up: major full height, mid 16px, minor 8px
function renderHorizontalTick(tick: Tick, rulerInset: number, spreadOriginCanvasX: number) {
  const isSpreadOriginTick = Math.abs(tick.anchorU - spreadOriginCanvasX) < 0.01

  return (
    <React.Fragment key={rulerTickReactKey('h', tick)}>
      <div
        className={isSpreadOriginTick ? undefined : 'border-l'}
        style={{
          position: 'absolute',
          // Explicit offset for the origin tick, not `transform: translateX(-50%)` —
          // same fix as the spine bar in `double-spread-document.tsx`: a percentage
          // transform on a 1px-wide element resolves against its own rendered width,
          // which can round asymmetrically under zoom/DPR and leave the tick reading
          // entirely on one side of the seam instead of split evenly across it.
          left: isSpreadOriginTick ? tick.pos + rulerInset - 0.5 : tick.pos + rulerInset,
          bottom: 0,
          height: getHorizontalTickHeight(tick),
          ...(isSpreadOriginTick
            ? {
                width: 1,
                backgroundColor: '#000',
              }
            : {
                borderColor: '#000',
              }),
        }}
      />
      {tick.label && (
        <div
          className="text-[10px] text-[color:var(--doc-label)] select-none"
          style={{
            position: 'absolute',
            left: tick.pos + rulerInset + (isSpreadOriginTick ? 5 : 3),
            bottom: 2,
          }}
        >
          {tick.label}
        </div>
      )}
    </React.Fragment>
  )
}

// Vertical ruler ticks: on the right edge of the ruler; labels to the left of the ticks
function renderVerticalTick(tick: Tick, rulerInset: number) {
  return (
    <React.Fragment key={rulerTickReactKey('v', tick)}>
      <div
        className="border-t"
        style={{
          position: 'absolute',
          top: tick.pos + rulerInset,
          right: 0,
          width: getVerticalTickWidth(tick),
          borderColor: '#000',
        }}
      />
      {tick.label && (
        <div
          className="text-[10px] text-[color:var(--doc-label)] select-none leading-none"
          style={{
            position: 'absolute',
            // Offset so number sits in the middle of the first minor tick below the major tick
            top: tick.pos + rulerInset + 7,
            left: 2,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
        >
          {tick.label}
        </div>
      )}
    </React.Fragment>
  )
}

/**
 * One pinned ruler (top = `horizontal`, left = `vertical`). The tick strip is translated by the
 * scroll listener through `stripRef`; mouse-down starts dragging out a new guide.
 */
export function CanvasRuler({
  axis,
  ticks,
  stripMinPx,
  stripRef,
  spreadOriginCanvasX,
  onRulerMouseDown,
}: {
  axis: RulerAxis
  /** Viewport-filtered ticks for this axis. */
  ticks: Tick[]
  /** Strip length along the axis (covers the whole scrollable range). */
  stripMinPx: number
  stripRef: React.MutableRefObject<HTMLDivElement | null>
  /** Canvas X of the spread origin (ruler 0); the horizontal ruler draws that tick specially. */
  spreadOriginCanvasX: number
  onRulerMouseDown: (e: React.MouseEvent, axis: RulerAxis) => void
}) {
  const isHorizontal = axis === 'horizontal'
  // Offset by the other ruler's thickness so ticks align with scroller content.
  const rulerInset = (isHorizontal ? V_RULER_THICKNESS_PX : H_RULER_THICKNESS_PX) + 0.5

  return (
    <div
      className={
        isHorizontal
          ? 'doc-ruler absolute left-0 top-0 right-0 border-b z-10 overflow-hidden cursor-ns-resize'
          : 'doc-ruler absolute left-0 top-0 bottom-0 border-r z-10 overflow-hidden cursor-ew-resize'
      }
      style={isHorizontal ? { height: H_RULER_THICKNESS_PX } : { width: V_RULER_THICKNESS_PX }}
      onMouseDown={(e) => onRulerMouseDown(e, axis)}
    >
      <div className="relative w-full h-full overflow-hidden">
        <div
          ref={stripRef}
          className={
            isHorizontal ? 'absolute top-0 left-0 h-full' : 'absolute top-0 right-0 w-full'
          }
          style={
            isHorizontal
              ? { width: stripMinPx, willChange: 'transform' }
              : { height: stripMinPx, willChange: 'transform' }
          }
        >
          {isHorizontal
            ? ticks.map((tick) => renderHorizontalTick(tick, rulerInset, spreadOriginCanvasX))
            : ticks.map((tick) => renderVerticalTick(tick, rulerInset))}
        </div>
      </div>
    </div>
  )
}
